import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import {
  FaBroadcastTower,
  FaCalendar,
  FaCalendarPlus,
  FaDotCircle,
  FaList,
  FaMusic,
  FaPlusCircle,
  FaTv,
} from "react-icons/fa";

import { useGetMyAccount } from "../api/queries";
import { hasAdminRights, isSelfServiceCatalogue, isTunecoreCatalogue } from "../auth/shared";
import SideNavItem from "../components/SideNavItem";
// import AppState from "../store/appstate";
import useAppStore from "../store/appStateZustand";

function SubSideNav({ menuItems }) {
  return (
    <>
      {menuItems.map(item => (
        <SideNavItem
          key={item.id}
          id={item.id}
          icon={item.icon}
          to={item.to}
          exact={item.exact}
          title={item.title}
          subtitle={item.subtitle}
          disabled={item.disabled}
        />
      ))}
    </>
  );
}

export default SubSideNav;

function useMenuData() {
  const { t } = useTranslation();
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const selectedCatalogue = useAppStore(s => s.selectedCatalogue);
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const adminRights = hasAdminRights(myAccount?.data);
  const isUserInSelfServiceCatalogue = isSelfServiceCatalogue(selectedCatalogue);
  const isUserTunecoreCatalogue = isTunecoreCatalogue(selectedCatalogue);

  return {
    t,
    adminRights,
    isUserInSelfServiceCatalogue,
    isUserTunecoreCatalogue,
  };
}

function OpportunitiesMenu() {
  const { t, adminRights } = useMenuData();

  const opportunitiesMenuItems = [
    {
      id: "btnViewOpportunities",
      icon: <FaDotCircle />,
      to: adminRights ? "/opportunities/" : "",
      exact: true,
      title: t("common:overview"),
      subtitle: t("other:opportunities-text"),
      disabled: !adminRights,
    },
    {
      id: "btnApplyOpportunities",
      icon: <FaPlusCircle />,
      to: adminRights ? "/opportunities/apply" : "",
      title: t("other:apply"),
      subtitle: t("other:opportunities-menu-subheader2"),
      disabled: !adminRights,
    },
  ];

  return <SubSideNav menuItems={opportunitiesMenuItems} />;
}

function SyncsMenu() {
  const { t, adminRights } = useMenuData();

  const syncsMenuItems = [
    {
      id: "btnViewSyncs",
      icon: <FaTv />,
      to: adminRights ? "/syncs/" : "",
      exact: true,
      title: t("common:overview"),
      subtitle: t("other:syncs-menu-subheader1"),
      disabled: !adminRights,
    },
    {
      id: "btnSubmitSyncs",
      icon: <FaPlusCircle />,
      to: adminRights ? "/syncs/apply" : "",
      title: t("other:apply"),
      subtitle: t("other:syncs-menu-subheader2"),
      disabled: !adminRights,
    },
  ];

  return <SubSideNav menuItems={syncsMenuItems} />;
}

function BroadcastsMenu() {
  const { t, adminRights } = useMenuData();

  const broadcastMenuItems = [
    {
      id: "btnViewBroadcasts",
      icon: <FaBroadcastTower />,
      to: "/broadcasts",
      exact: true,
      title: t("other:broadcasts-header"),
      subtitle: t("other:view-broadcasts-subheader"),
    },
    {
      id: "btnAddBroadcasts",
      icon: <FaPlusCircle />,
      to: adminRights ? "/broadcasts/add" : "",
      title: t("common:add"),
      subtitle: t("other:add-broadcast-details-subheader"),
      disabled: !adminRights,
    },
  ];

  return <SubSideNav menuItems={broadcastMenuItems} />;
}

function SongsMenu() {
  const { t, adminRights } = useMenuData();
  const flags = useFlags();

  // Get UNK Publishing status
  const isRoughTrade = useAppStore(s =>
    s?.clientDisplayName?.includes("Rough Trade Publishing"),
  );

  const songsMenuItems = [
    {
      id: "btnViewSongs",
      icon: <FaMusic />,
      to: "/songs",
      exact: true,
      title: t("common:overview"),
      subtitle: t("other:view-songs-subheader"),
    },
    {
      id: "btnAddSong",
      icon: <FaPlusCircle />,
      to: adminRights ? "/songs/create" : "",
      exact: true,
      title: t("songs:add-song"),
      subtitle: t("other:add-song-subheader"),
      disabled: !adminRights,
    },
  ];

  if (adminRights && (!isRoughTrade) && flags["pd-add-album-feature"]) {
    songsMenuItems.push({
      id: "btnAddMultiSong",
      icon: <FaList />,
      to: "/songs/create-album",
      exact: true,
      title: t("other:add-album-header"),
      subtitle: t("other:add-album-subheader"),
    });
  }

  if (adminRights && (!isRoughTrade) && flags["pd-add-playlist-feature"]) {
    songsMenuItems.push({
      id: "btnAddPlaylist",
      icon: <FaList />,
      to: "/songs/create-playlist",
      exact: true,
      title: t("playlist:add-playlist-header", "Add Playlist"),
      subtitle: t("playlist:add-playlist-subheader", "Import a playlist from Spotify"),
    });
  }

  return <SubSideNav menuItems={songsMenuItems} />;
}

function GigsMenu() {
  const { t, adminRights } = useMenuData();

  const gigsMenuItems = [
    {
      id: "btnGigsMenuOverview",
      icon: <FaCalendar />,
      to: "/gigs/",
      exact: true,
      title: t("other:gigs-menu-overview"),
      subtitle: t("other:gigs-menu-overview-subtitle2"),
    },
    {
      id: "btnGigsMenuClaim",
      icon: <FaCalendarPlus />,
      to: adminRights ? "/gigs/claim" : "",
      exact: true,
      title: t("other:gigs-menu-claim"),
      subtitle: t("other:gigs-menu-claim-subtitle"),
      disabled: !adminRights,
    },
    {
      id: "btnGigsMenuSetlists",
      icon: <FaList />,
      to: adminRights ? "/gigs/setlists" : "",
      exact: true,
      title: t("other:gigs-menu-setlists"),
      subtitle: t("other:gigs-menu-setlists-subtitle"),
      disabled: !adminRights,
    },
    {
      id: "btnGigsMenuCreate",
      icon: <FaPlusCircle />,
      to: adminRights ? "/gigs/setlists/create" : "",
      title: t("other:gigs-menu-add-setlist"),
      subtitle: t("other:gigs-menu-add-setlist-subtitle"),
      disabled: !adminRights,
    },
  ];

  return <SubSideNav menuItems={gigsMenuItems} />;
}

export {
  BroadcastsMenu,
  GigsMenu,
  OpportunitiesMenu,
  SongsMenu,
  SyncsMenu,
};
